import { z } from "zod";
import { WORKFLOWS } from "../orchestration";
import { csvImportWorkflow } from "./csv-import";
import { githubEmailWorkflow } from "./email-enrichment";
import { taskDefinitions } from "./tasks";

const catalog = [
  {
    key: "github.enrich_person",
    name: "GitHub Email Enrich",
    description:
      "Look up a GitHub username and discover additional public commit emails using an authenticated connection.",
    schedule: "Manual",
  },
  {
    key: "reddit.sync_subreddit",
    name: "Reddit · Sync subreddit",
    description: "Collect archived posts and comments matching the subreddit’s keywords.",
    schedule: "Manual",
  },
  {
    key: "github.sync_repository",
    name: "GitHub · Sync repository",
    description: "Collect stargazers and community activity.",
    schedule: "Manual",
  },
  {
    key: "github.sync_stargazers",
    name: "GitHub · Sync stargazers",
    description:
      "Find the people paying attention. Collect the roster where available, or retain public star events for external repositories.",
    schedule: "Manual",
  },
  {
    key: "github.sync_issues",
    name: "GitHub · Sync issues",
    description:
      "Import issues, then follow new issues, closures, reopenings, and updates. Every import resumes from its last completed page.",
    schedule: "Manual",
  },
  {
    key: "github.sync_activity",
    name: "GitHub · Sync community activity",
    description:
      "Refresh forks, watchers, and discussion comments. Each collection retains its last good data if access is unavailable.",
    schedule: "Manual",
  },
  {
    key: "github.backfill_stars",
    name: "GitHub · Backfill archived stars",
    description:
      "Recover star history from the public GitHub event archive for repositories whose roster is not readable. A one-off import bounded by the moment the source was connected; it is never scheduled.",
    schedule: "One-off",
  },
  {
    key: "slack.send_records",
    name: "Slack · Send people",
    description:
      "Post selected people, with the signals they were chosen from, to a connected Slack channel. Runs when people are sent; never scheduled.",
    schedule: "Manual",
  },
  {
    key: "webhook.send_records",
    name: "Webhook · Send people",
    description:
      "Deliver selected people to a connected webhook destination with a signed payload. Runs when people are sent; never scheduled.",
    schedule: "Manual",
  },
] as const;

const BY_NAME = new Map(Object.values(taskDefinitions).map((task) => [task.name, task]));

/**
 * The Library's view of a workflow: its description, plus the steps it will
 * actually run.
 *
 * Steps are read from `WORKFLOWS` rather than listed here. Listing them twice
 * is how the Library came to offer a workflow the runtime had never heard of,
 * and how it advertised a step that no longer ran.
 */
export const workflows = catalog.map((entry) => ({
  ...entry,
  tasks: WORKFLOWS[entry.key].tasks.map((name) => BY_NAME.get(name) ?? { key: name, name }),
}));

export type WorkflowCatalogEntry = (typeof workflows)[number];
export function workflowName(key: string) {
  return (
    (key === githubEmailWorkflow.key
      ? githubEmailWorkflow.name
      : key === csvImportWorkflow.key
        ? csvImportWorkflow.name
        : workflows.find((w) => w.key === key)?.name) || key
  );
}

export type RunDetail = import("zod").infer<typeof import("./api").runDetailSchema>;

export const workflowKeySchema = z.enum(workflows.map((workflow) => workflow.key));
